import { Button, DataGrid, TextBox, Popup, CheckBox } from "devextreme-react";
import { Column, Editing, Lookup, Scrolling, SearchPanel } from "devextreme-react/data-grid";
import { useState, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { deleteUserRole, updateUserRole } from "../../actions";
import ModalContainer from "../../common/ModalContainer";
import { modalActions } from "./modalActions";
import PermissionsByUser from "./Modals/PermissionsByUser/PermissionsByUser";
import "./adminUserRoleBody.css";


const AdminUserRoleBody = (props) => {
    const data = props.data;
    const users = useSelector(state => state.admin.users);
    const [state, setState] = useState({
        "open": {
            "permissionsByUser": false,
            "addUsers": false
        },
        "userId": 0
    });
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [mainRoleForNew, setMainRoleForNew] = useState(false);
    
    const usersDs = data.users;
    const dispatch = useDispatch();

    // Get available users (not already in role)
    const availableUsers = useMemo(() => {
        const assignedUserIds = new Set(usersDs.map(u => u.idUser));
        return users.filter(u => !assignedUserIds.has(u.Id));
    }, [users, usersDs]);

    // Filter available users by search term
    const filteredAvailableUsers = useMemo(() => {
        if (!searchTerm) return availableUsers;
        const term = searchTerm.toLowerCase();
        return availableUsers.filter(u => 
            u.DisplayName?.toLowerCase().includes(term) || 
            u.Email?.toLowerCase().includes(term)
        );
    }, [availableUsers, searchTerm]);

    const onRowRemoved = (e) => {
        console.log(e);
    };

    const onSaving = (e) => {
        const change = e.changes[0];
        if (change.type === "remove") {
            const deletedRow = change.key;
            const userId = deletedRow.idUser;
            const roleId = deletedRow.idRole;
            dispatch(deleteUserRole(userId, roleId));
        }
        else if (change.type === "insert") {
            const formData = e.changes[0].data;
            const user = users.filter(x => x.Id === formData.id)[0];
            const postData = { idRole: data.id, idUser: formData.id, mainRole: formData.mainRole };
            dispatch(updateUserRole(postData));
        }
    };

    const handleOpenAddUsers = () => {
        setState({ ...state, open: { ...state.open, addUsers: true } });
        setSelectedUsers([]);
        setSearchTerm("");
        setMainRoleForNew(false);
    };

    const handleCloseAddUsers = () => {
        setState({ ...state, open: { ...state.open, addUsers: false } });
        setSelectedUsers([]);
        setSearchTerm("");
    };

    const handleToggleUser = (userId) => {
        setSelectedUsers(prev => 
            prev.includes(userId) 
                ? prev.filter(id => id !== userId)
                : [...prev, userId]
        );
    };

    const handleAddSelectedUsers = () => {
        selectedUsers.forEach(userId => {
            const postData = { 
                idRole: data.id, 
                idUser: userId, 
                mainRole: mainRoleForNew 
            };
            dispatch(updateUserRole(postData));
        });
        handleCloseAddUsers();
    };

    const handleRemoveUser = (userId, roleId) => {
        if (window.confirm("Are you sure you want to remove this user from the role?")) {
            dispatch(deleteUserRole(userId, roleId));
        }
    };

    const { ActionsButtons, handleCloseDialog } = modalActions(setState, state, "permissionsByUser", <i className="dx-icon-preferences"></i>, "userId", "idUser");


    return (
        <div className="admin-user-role-body">
            <div className="role-card">
                <div className="card-header">
                    <h3>Users in Role ({usersDs.length})</h3>
                    <Button
                        text="Add Users"
                        icon="add"
                        type="default"
                        stylingMode="contained"
                        onClick={handleOpenAddUsers}
                    />
                </div>

                {usersDs.length === 0 && (
                    <div className="info-alert">
                        <i className="dx-icon-info"></i>
                        <span>No users assigned to this role yet. Click "Add Users" to get started.</span>
                    </div>
                )}

                <div id="kitsGrid">
                    <DataGrid
                        id="gridContainer"
                        dataSource={usersDs}
                        showBorders={true}
                        onSaving={onSaving}
                        onRowRemoved={onRowRemoved}
                        height={"30vh"}
                    >
                        <SearchPanel visible={true} width={240} placeholder="Search users..." />
                        <Editing
                            mode="form"
                            allowAdding={false}
                            allowDeleting={true}
                            useIcons={true}
                        />
                        <Scrolling mode="virtual" />
                        <Column dataField="id" caption="id" visible={false}>
                            <Lookup
                                dataSource={users}
                                valueExpr="Id"
                                displayExpr="DisplayName"
                            />
                        </Column>
                        <Column dataField="idUser" caption="idUser" visible={false} />
                        <Column dataField="displayName" caption="Name" />
                        <Column dataField="email" caption="Email" />
                        <Column 
                            dataField="mainRole" 
                            caption="Main Role" 
                            dataType={"boolean"}
                            cellRender={(data) => (
                                data.value ? (
                                    <span className="role-badge primary">Main</span>
                                ) : (
                                    <span className="role-badge secondary">Secondary</span>
                                )
                            )}
                        />
                        <Column 
                            type="buttons" 
                            caption="Actions" 
                            allowEditing={false} 
                            minWidth={100}
                            cellRender={(data) => (
                                <div className="action-buttons">
                                    <button
                                        className="action-btn primary"
                                        onClick={() => {
                                            setState({ 
                                                ...state, 
                                                open: { ...state.open, permissionsByUser: true }, 
                                                userId: data.data.idUser 
                                            });
                                        }}
                                        title="View Permissions"
                                    >
                                        <i className="dx-icon-preferences"></i>
                                    </button>
                                    <button
                                        className="action-btn danger"
                                        onClick={() => handleRemoveUser(data.data.idUser, data.data.idRole)}
                                        title="Remove User"
                                    >
                                        <i className="dx-icon-trash"></i>
                                    </button>
                                </div>
                            )}
                        />
                    </DataGrid>
                </div>
            </div>

            {/* Add Users Dialog */}
            <Popup
                visible={state.open.addUsers}
                onHiding={handleCloseAddUsers}
                dragEnabled={false}
                closeOnOutsideClick={true}
                showTitle={true}
                title="Add Users to Role"
                width={700}
                height="auto"
            >
                <div className="popup-content">
                    <TextBox
                        placeholder="Search by name or email..."
                        value={searchTerm}
                        onValueChanged={(e) => setSearchTerm(e.value)}
                        mode="search"
                        width="100%"
                        stylingMode="outlined"
                    />

                    {selectedUsers.length > 0 && (
                        <div className="success-alert">
                            <strong>{selectedUsers.length}</strong> user(s) selected
                        </div>
                    )}

                    {filteredAvailableUsers.length === 0 ? (
                        <div className="info-alert">
                            {searchTerm 
                                ? "No users found matching your search."
                                : "All users are already assigned to this role."}
                        </div>
                    ) : (
                        <div className="users-list">
                            {filteredAvailableUsers.map(user => (
                                <div
                                    key={user.Id}
                                    className={`user-item ${selectedUsers.includes(user.Id) ? 'selected' : ''}`}
                                    onClick={() => handleToggleUser(user.Id)}
                                >
                                    <div className="user-info">
                                        <div className="user-name">{user.DisplayName}</div>
                                        <div className="user-email">{user.Email}</div>
                                    </div>
                                    {selectedUsers.includes(user.Id) && (
                                        <span className="selected-badge">Selected</span>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    {selectedUsers.length > 0 && (
                        <div className="main-role-section">
                            <div className="section-label">
                                <span>Set as Main Role</span>
                                <i className="dx-icon-info" title="If checked, this role will be set as the main role for the selected users"></i>
                            </div>
                            <CheckBox
                                text="Make this the primary role for selected users"
                                value={mainRoleForNew}
                                onValueChanged={(e) => setMainRoleForNew(e.value)}
                            />
                        </div>
                    )}

                    <div className="dialog-actions">
                        <Button
                            text="Cancel"
                            onClick={handleCloseAddUsers}
                            stylingMode="outlined"
                        />
                        <Button
                            text={`Add ${selectedUsers.length > 0 ? `(${selectedUsers.length})` : ''} User${selectedUsers.length !== 1 ? 's' : ''}`}
                            icon="add"
                            onClick={handleAddSelectedUsers}
                            disabled={selectedUsers.length === 0}
                            type="default"
                            stylingMode="contained"
                        />
                    </div>
                </div>
            </Popup>

            <ModalContainer 
                open={state.open.permissionsByUser} 
                modalId="permissionsByUser" 
                onClose={handleCloseDialog} 
                title={<h3>User Permissions</h3>}
            >
                <PermissionsByUser userId={state.userId} />
            </ModalContainer>
        </div>
    );
};

export default AdminUserRoleBody;